import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {type ScribeArtifact, useScribeArtifacts} from '@github-ui/scribe-artifacts'
import {ChevronDownIcon, ChevronRightIcon} from '@primer/octicons-react'
import {CounterLabel, IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {Suspense, useCallback, useEffect, useMemo, useState} from 'react'
import {graphql, useLazyLoadQuery} from 'react-relay'

import {TEST_IDS} from '../../constants/test-ids'
import type {ArtifactsSectionQuery as ArtifactsSectionQueryType} from './__generated__/ArtifactsSectionQuery.graphql'
import type {PanelArtifact} from './ArtifactPanelContent'
import {PersistedArtifactRow} from './ArtifactRow'
import styles from './ArtifactsSection.module.css'
import {ArtifactsSidePanelOverlay} from './ArtifactsSidePanelOverlay'
import {groupArtifactsByType} from './group-artifacts'
import type {IssueArtifact} from './types'
import {useArtifactsPanelState} from './use-artifacts-panel-state'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const ArtifactsSectionQuery = graphql`
  query ArtifactsSectionQuery($id: ID!) {
    node(id: $id) {
      ... on Issue {
        databaseId
      }
    }
  }
`

/** Maps a Scribe API artifact (snake_case payload) onto the section's view model. */
function mapScribeArtifact(artifact: ScribeArtifact, index: number): IssueArtifact {
  const numericId = typeof artifact.id === 'number' ? artifact.id : Number(artifact.id)
  return {
    id: Number.isFinite(numericId) ? numericId : index + 1,
    number: artifact.number ?? index + 1,
    type: artifact.type === 'plan' || artifact.type === 'link' ? artifact.type : 'generic',
    name: artifact.name,
    body: artifact.body ?? '',
    bodyHTML: artifact.body_html ?? undefined,
    actor: {login: artifact.creator?.login ?? '', avatarUrl: ''},
    createdAt: artifact.created_at ?? '',
    updatedAt: artifact.updated_at ?? '',
  }
}

type ArtifactsSectionContentProps = {
  issueId: string
  onSelectArtifact: (artifact: PanelArtifact) => void
  onArtifactDelete?: (id: number) => void
  onCountChange: (count: number) => void
}

function ArtifactsSectionContent({
  issueId,
  onSelectArtifact,
  onArtifactDelete,
  onCountChange,
}: ArtifactsSectionContentProps) {
  const data = useLazyLoadQuery<ArtifactsSectionQueryType>(ArtifactsSectionQuery, {id: issueId})

  const issueDatabaseId = data.node?.databaseId ?? 0
  const {data: scribeArtifacts} = useScribeArtifacts({
    resourceType: 'issue',
    resourceId: issueDatabaseId,
    enabled: issueDatabaseId > 0,
  })

  const persistedArtifacts = useMemo<IssueArtifact[]>(
    () => groupArtifactsByType((scribeArtifacts ?? []).map(mapScribeArtifact)),
    [scribeArtifacts],
  )

  const totalArtifacts = persistedArtifacts.length

  useEffect(() => {
    onCountChange(totalArtifacts)
  }, [totalArtifacts, onCountChange])

  if (totalArtifacts === 0) {
    return <div className={styles.EmptyState}>Add files, links, or plans to this issue.</div>
  }

  return (
    <ul className={styles.List} aria-label="Artifacts">
      {persistedArtifacts.map(artifact => (
        <PersistedArtifactRow
          key={`${artifact.type}:${artifact.id}`}
          id={artifact.id}
          type={artifact.type}
          name={artifact.name}
          body={artifact.body}
          bodyHTML={artifact.bodyHTML}
          onSelect={onSelectArtifact}
          onDelete={onArtifactDelete}
        />
      ))}
    </ul>
  )
}

type ArtifactsSectionProps = {
  issueId: string
  onArtifactDelete?: (id: number) => void
}

export function ArtifactsSection({issueId, onArtifactDelete}: ArtifactsSectionProps) {
  const [isExpanded, setIsExpanded] = useState<boolean | null>(null)
  const [artifactCount, setArtifactCount] = useState(0)

  const {
    activeArtifactId,
    activeArtifact,
    tabs,
    lastRowTriggerRef,
    selectArtifact: handleSelectArtifact,
    activate: handleActivate,
    closePanel: handleClosePanel,
    promoteOverflow: handlePromoteOverflow,
    closeTab: handleCloseTab,
  } = useArtifactsPanelState()

  // Default expanded when artifacts exist, collapsed when empty. User toggle wins.
  const effectiveExpanded = isExpanded ?? artifactCount > 0

  const toggleExpanded = useCallback(() => {
    setIsExpanded(!effectiveExpanded)
  }, [effectiveExpanded])

  if (!isFeatureEnabled('issue_artifacts_section')) return null

  return (
    <div
      className={clsx(styles.Container, isFeatureEnabled('issue_inline_avatars') && styles.inlineAvatars)}
      data-testid={TEST_IDS.issueArtifactsSection}
    >
      <div className={styles.Header}>
        <div className={styles.TitleGroup}>
          <IconButton
            icon={effectiveExpanded ? ChevronDownIcon : ChevronRightIcon}
            variant="invisible"
            size="small"
            aria-label={effectiveExpanded ? 'Collapse artifacts' : 'Expand artifacts'}
            aria-expanded={effectiveExpanded}
            onClick={toggleExpanded}
          />
          <span className={styles.TitleLabel}>Artifacts</span>
          {artifactCount > 0 && <CounterLabel>{artifactCount}</CounterLabel>}
        </div>
      </div>

      <div
        className={clsx(styles.Body, !effectiveExpanded && styles.collapsed)}
        inert={!effectiveExpanded || undefined}
      >
        <div className={styles.BodyInner}>
          <ErrorBoundary fallback={null} critical={false} boundaryName="ArtifactsSection">
            <Suspense fallback={null}>
              <ArtifactsSectionContent
                issueId={issueId}
                onSelectArtifact={handleSelectArtifact}
                onArtifactDelete={onArtifactDelete}
                onCountChange={setArtifactCount}
              />
            </Suspense>
          </ErrorBoundary>
        </div>
      </div>

      {activeArtifactId !== null && (
        <ArtifactsSidePanelOverlay
          open
          onClose={handleClosePanel}
          tabs={tabs}
          activeId={activeArtifactId}
          activeArtifact={activeArtifact}
          onActivate={handleActivate}
          onPromoteOverflowTab={handlePromoteOverflow}
          onCloseTab={handleCloseTab}
          returnFocusRef={lastRowTriggerRef}
        />
      )}
    </div>
  )
}
